/**
 * One hour per programmed weekday, one row each — the mock's `timesRows(filled)` block.
 *
 * A vertical list rather than the three-across inline variant, by the owner's decision of 22
 * September 2026: the label reads „Poniedziałek · Push" and a three-column grid gave it a third of
 * the pane to say that in. The mock's own non-inline `.time` is the shape — the label on the left,
 * the hour on the right at a fixed width — and it is the one that survives a plan training five
 * days a week.
 *
 * Which slots exist is `model/stationaryTimes.ts`'s answer, and it is not simply "the weekdays": a
 * plan can carry a day with no declared weekday, whose time the generator resolves through the
 * fallback slot instead.
 *
 * `required` on every input so a browser that supports the constraint says so at the control. The
 * rule is enforced again in `assignmentRequest` — an empty list is a 400 — because `required` on a
 * control inside a dialog with no `<form>` is a hint, not a gate.
 */

import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import type { TimeSlotView } from '../lib/useAssignFlow';

import styles from './AssignPlanDialog.module.css';

interface TimesListProps {
  readonly slots: readonly TimeSlotView[];
  readonly onChange: (slot: number, time: string) => void;
  readonly locked: boolean;
  readonly reasonId: string | null;
  /** The field's own help line — "one hour per programmed weekday" — when it is drawn. */
  readonly helpId: string | null;
}

export function TimesList({ slots, onChange, locked, reasonId, helpId }: TimesListProps) {
  const { t } = useTranslation();
  const prefix = useId();

  return (
    <div className={styles.times}>
      {slots.map((slot) => {
        const inputId = `${prefix}-${String(slot.slot)}`;
        const hintId = `${inputId}-hint`;
        return (
          <div key={slot.slot} className={styles.time}>
            <span className={styles.timeText}>
              <label className={styles.timeLabel} htmlFor={inputId}>
                {slot.label}
              </label>
              {slot.hint !== null && (
                <span id={hintId} className={styles.timeHint}>
                  {slot.hint}
                </span>
              )}
            </span>
            <input
              id={inputId}
              className={styles.timeInput}
              type="time"
              required
              value={slot.time}
              placeholder={t('web:assign.times.placeholder')}
              aria-disabled={locked ? 'true' : undefined}
              aria-describedby={[slot.hint === null ? '' : hintId, reasonId, helpId]
                .filter(Boolean)
                .join(' ')}
              onChange={(event) => {
                if (!locked) onChange(slot.slot, event.target.value);
              }}
            />
          </div>
        );
      })}
    </div>
  );
}
